import { Link } from "wouter";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import logoImage from "@/assets/logo.png";

export const TERMS_VERSION = "v1.1";
export const TERMS_DATE_ISO = "2026-05-08";

type Locale = "es" | "en";
type DocType = "terms" | "privacy";

interface LegalPageProps {
  locale: Locale;
  doc: DocType;
}

const COPY = {
  es: {
    backHome: "Volver al inicio",
    versionLabel: "Versión",
    lastUpdatedLabel: "Última actualización",
    disclaimer:
      "Este documento es la primera versión publicada por el equipo de CourtVibe. " +
      "No sustituye el asesoramiento de un abogado. Si necesitas una revisión " +
      "jurídica formal, consulta a un profesional.",
    contact: "Contacto",
    contactEmail: "contacto@courtvibe.app",
    operator:
      "CourtVibe es operado a título personal por Javier Piña, persona física " +
      "residente en la República Dominicana. Cualquier referencia a " +
      "\"CourtVibe\", \"nosotros\" o \"el servicio\" se refiere a este operador.",
    minAge: "Edad mínima: 16 años.",
    crossLinkPrivacy: "Política de Privacidad",
    crossLinkTerms: "Términos de Servicio",
    enLink: "English",
  },
  en: {
    backHome: "Back to home",
    versionLabel: "Version",
    lastUpdatedLabel: "Last updated",
    disclaimer:
      "This is the first version published by the CourtVibe team. It is not a " +
      "substitute for legal advice. If you need formal legal review, please " +
      "consult a qualified attorney.",
    contact: "Contact",
    contactEmail: "contacto@courtvibe.app",
    operator:
      "CourtVibe is operated personally by Javier Piña, an individual residing " +
      "in the Dominican Republic. Any reference to \"CourtVibe\", \"we\", or " +
      "\"the service\" means this operator.",
    minAge: "Minimum age: 16 years old.",
    crossLinkPrivacy: "Privacy Policy",
    crossLinkTerms: "Terms of Service",
    enLink: "Español",
  },
} as const;

function formatDate(iso: string, locale: Locale): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat(locale === "es" ? "es-ES" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}

export function LegalPage({ locale, doc }: LegalPageProps) {
  const t = COPY[locale];
  const title =
    doc === "terms"
      ? locale === "es"
        ? "Términos de Servicio"
        : "Terms of Service"
      : locale === "es"
        ? "Política de Privacidad"
        : "Privacy Policy";

  const altLocale: Locale = locale === "es" ? "en" : "es";
  const altPath =
    doc === "terms"
      ? altLocale === "es"
        ? "/terminos"
        : "/terms"
      : altLocale === "es"
        ? "/privacidad"
        : "/privacy";

  const crossLinkPath =
    doc === "terms"
      ? locale === "es"
        ? "/privacidad"
        : "/privacy"
      : locale === "es"
        ? "/terminos"
        : "/terms";
  const crossLinkLabel =
    doc === "terms" ? t.crossLinkPrivacy : t.crossLinkTerms;

  return (
    <div className="min-h-screen bg-background">
      <header
        className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="container max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm font-medium hover:text-primary"
            data-testid="link-legal-home"
          >
            <ArrowLeft className="h-4 w-4" />
            <img src={logoImage} alt="CourtVibe" className="h-6 w-6 rounded-full" />
            <span>{t.backHome}</span>
          </Link>
          <div className="ml-auto">
            <Link
              href={altPath}
              className="text-xs underline text-muted-foreground hover:text-foreground"
              data-testid="link-legal-locale-switch"
            >
              {t.enLink}
            </Link>
          </div>
        </div>
      </header>

      <main className="container max-w-3xl mx-auto px-4 py-8 safe-area-bottom">
        <div
          className="mb-6 flex items-start gap-3 rounded-lg border border-amber-300/50 bg-amber-50 p-4 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-700/40"
          data-testid="banner-legal-disclaimer"
        >
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed">{t.disclaimer}</p>
        </div>

        <h1
          className="text-3xl font-bold mb-2"
          data-testid={`text-legal-title-${doc}-${locale}`}
        >
          {title}
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          {t.versionLabel} {TERMS_VERSION} · {t.lastUpdatedLabel}:{" "}
          {formatDate(TERMS_DATE_ISO, locale)}
        </p>

        <article className="prose prose-sm sm:prose-base dark:prose-invert max-w-none space-y-6">
          {doc === "terms" ? (
            locale === "es" ? <TermsES /> : <TermsEN />
          ) : locale === "es" ? (
            <PrivacyES />
          ) : (
            <PrivacyEN />
          )}
        </article>

        <footer className="mt-12 border-t pt-6 text-sm text-muted-foreground">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link href={crossLinkPath} className="underline hover:text-foreground">
              {crossLinkLabel}
            </Link>
            <Link href={altPath} className="underline hover:text-foreground">
              {t.enLink}
            </Link>
            <a
              href={`mailto:${t.contactEmail}`}
              className="underline hover:text-foreground"
            >
              {t.contact}: {t.contactEmail}
            </a>
          </div>
          <p className="mt-4 text-xs">© 2026 CourtVibe — Javier Piña (RD).</p>
        </footer>
      </main>
    </div>
  );
}

/* ---------------- Spanish — Terms ---------------- */
function TermsES() {
  return (
    <>
      <p>
        Estos Términos regulan el uso de <strong>CourtVibe</strong>, una aplicación
        web y móvil dedicada a la comunidad de pádel (registro de partidos,
        rankings, ligas privadas y funciones sociales). Al usar CourtVibe
        confirmas que aceptas estos Términos.
      </p>

      <h2>1. Operador y contacto</h2>
      <p>{COPY.es.operator}</p>
      <p>
        Para cualquier consulta, escribe a{" "}
        <a href="mailto:contacto@courtvibe.app">contacto@courtvibe.app</a>.
      </p>

      <h2>2. Edad mínima</h2>
      <p>
        Para crear una cuenta debes tener al menos <strong>16 años</strong>. Si
        eres menor de 18, recomendamos contar con autorización de tu
        representante legal.
      </p>

      <h2>3. Cuenta de usuario</h2>
      <ul>
        <li>
          El acceso se hace a través de Replit Auth (con proveedores como
          Google o Apple) o por correo electrónico.
        </li>
        <li>
          Eres responsable de mantener la confidencialidad de tus credenciales
          y de toda la actividad realizada desde tu cuenta.
        </li>
        <li>
          Debes proporcionar información veraz en tu perfil (nombre, fecha de
          nacimiento, nivel, etc.).
        </li>
      </ul>

      <h2>4. Uso aceptable</h2>
      <p>Al usar CourtVibe te comprometes a NO:</p>
      <ul>
        <li>Falsificar resultados de partidos o suplantar a otros jugadores.</li>
        <li>Acosar, amenazar o discriminar a otros usuarios.</li>
        <li>
          Intentar vulnerar la seguridad del servicio (scraping masivo,
          ingeniería inversa, ataques DoS, etc.).
        </li>
        <li>Subir contenido ilegal, ofensivo o que infrinja derechos de terceros.</li>
      </ul>
      <p>
        Podemos suspender o eliminar cuentas que incumplan estas reglas, sin
        previo aviso cuando sea necesario para proteger a la comunidad.
      </p>

      <h2>5. Contenido del usuario</h2>
      <p>
        Los partidos, comentarios, resultados, fotos de perfil y demás
        información que aportes siguen siendo tuyos. Al subirlos nos otorgas
        una licencia limitada, mundial y gratuita para mostrarlos dentro de
        CourtVibe a otros usuarios con los que interactúas (rivales, miembros de
        tu liga, etc.) y para operar el servicio.
      </p>

      <h2>6. Sistema de niveles y ELO (carácter informativo)</h2>
      <p>
        Los niveles (1.0 – 7.0), el ELO global y los rankings de CourtVibe
        tienen carácter <strong>exclusivamente informativo y recreativo</strong>.
        Son una herramienta de la comunidad, <strong>no constituyen un
        ranking oficial ni federativo</strong> y no sustituyen las
        clasificaciones de la Federación Dominicana de Pádel ni de
        cualquier otra federación, asociación o circuito profesional. Su
        uso para inscripciones, premios o decisiones formales es
        responsabilidad de quien los utilice.
      </p>
      <p>
        El cálculo es determinista pero puede ajustarse en el futuro para
        mejorar la precisión; los cambios se comunicarán dentro de la app
        cuando sean relevantes.
      </p>

      <h2>7. Disponibilidad y modelo de servicio</h2>
      <p>
        CourtVibe se ofrece "tal cual", sin garantía de disponibilidad continua.
        Podemos interrumpir, modificar o discontinuar funcionalidades en
        cualquier momento, especialmente durante esta fase inicial.
      </p>
      <p>
        Actualmente el servicio es <strong>gratuito</strong>. En el futuro
        podemos introducir <strong>planes de pago</strong> o funciones
        premium. Cualquier cambio sustancial al modelo (incluido cobrar por
        funciones que hoy son gratuitas) será comunicado dentro de la app o
        por correo electrónico con al menos <strong>15 días de antelación</strong>
        antes de su entrada en vigor, dándote la posibilidad de no
        continuar usando esas funciones.
      </p>

      <h2>8. Limitación de responsabilidad</h2>
      <p>
        En la máxima medida permitida por la ley, el operador no es
        responsable por daños indirectos, lucro cesante, pérdida de datos o
        cualquier otro perjuicio derivado del uso o imposibilidad de uso de
        CourtVibe. Las relaciones entre jugadores (encuentros presenciales,
        pagos, lesiones, etc.) son responsabilidad exclusiva de los
        usuarios.
      </p>
      <p>
        En todo caso, y sin perjuicio de lo anterior, la responsabilidad
        total acumulada del operador frente a cualquier usuario, por todas
        las reclamaciones derivadas o relacionadas con el uso de CourtVibe,
        queda limitada a un máximo de <strong>cien dólares de los Estados
        Unidos (US$100)</strong> o su equivalente en pesos dominicanos.
      </p>

      <h2>9. Eliminación de cuenta</h2>
      <p>
        Puedes eliminar tu cuenta en cualquier momento desde "Perfil →
        Eliminar cuenta". Se borran tus datos personales; los partidos
        registrados se conservan de forma anonimizada como{" "}
        <em>"Cuenta eliminada"</em> para preservar el historial de tus
        rivales. Más detalle en la Política de Privacidad.
      </p>

      <h2>10. Propiedad intelectual</h2>
      <p>
        La marca <em>CourtVibe</em>, el logo, el código y los diseños son
        propiedad del operador. No pueden copiarse ni usarse sin permiso
        previo por escrito.
      </p>

      <h2>11. Ley aplicable y jurisdicción</h2>
      <p>
        Estos Términos se rigen por las leyes de la <strong>República
        Dominicana</strong>, en particular la Ley 172-13 de Protección de
        Datos Personales. Cualquier controversia se someterá a los tribunales
        competentes de Santo Domingo.
      </p>

      <h2>12. Cambios en los Términos</h2>
      <p>
        Podemos actualizar estos Términos. Si los cambios son sustanciales,
        te avisaremos en la app o por correo y, cuando corresponda, te
        pediremos volver a aceptar.
      </p>

      <p className="text-sm text-muted-foreground">
        Versión {TERMS_VERSION} · {formatDate(TERMS_DATE_ISO, "es")}
      </p>
    </>
  );
}

/* ---------------- Spanish — Privacy ---------------- */
function PrivacyES() {
  return (
    <>
      <p>
        En <strong>CourtVibe</strong> tratamos tus datos personales con el cuidado
        que esperamos para los nuestros. Esta política explica qué datos
        recopilamos, para qué, con quién los compartimos y qué derechos tienes
        sobre ellos. Cumplimos la Ley 172-13 de Protección de Datos
        Personales de la República Dominicana.
      </p>

      <h2>1. Responsable del tratamiento</h2>
      <p>{COPY.es.operator}</p>
      <p>
        Contacto:{" "}
        <a href="mailto:contacto@courtvibe.app">contacto@courtvibe.app</a>.
      </p>

      <h2>2. Datos que recopilamos</h2>
      <ul>
        <li>
          <strong>Cuenta:</strong> nombre, apellido, correo, foto de perfil
          (cuando lo aportas o lo proporciona Google/Apple en el inicio de
          sesión).
        </li>
        <li>
          <strong>Perfil de jugador:</strong> teléfono, fecha de nacimiento,
          género, país, ciudad, club habitual, mano dominante, estilo de
          juego, años jugando, horarios y frecuencia.
        </li>
        <li>
          <strong>Actividad deportiva:</strong> partidos, resultados,
          confirmaciones, ligas, amistades, calendarios, logros.
        </li>
        <li>
          <strong>Datos técnicos:</strong> identificadores de sesión, tokens
          de notificaciones push, idioma del dispositivo, registros mínimos
          para depuración.
        </li>
        <li>
          <strong>Aceptación legal:</strong> guardamos la fecha y versión de
          los Términos y de esta Política aceptados.
        </li>
      </ul>

      <h2>3. Finalidades del tratamiento</h2>
      <ul>
        <li>Operar el servicio (rankings, ligas, partidos).</li>
        <li>Enviarte notificaciones relevantes (partidos pendientes, invitaciones).</li>
        <li>Atender consultas de soporte y prevenir abusos.</li>
        <li>Mejorar el producto (analítica agregada y depuración).</li>
        <li>Cumplir obligaciones legales aplicables.</li>
      </ul>

      <h2>4. Base legal</h2>
      <p>
        Tratamos los datos en base a tu consentimiento (al aceptar estos
        Términos y esta Política), la ejecución del servicio que solicitas y
        nuestro interés legítimo en mantener CourtVibe seguro y funcional.
      </p>

      <h2>5. Terceros que nos ayudan a operar</h2>
      <p>
        Algunos datos se procesan en infraestructura de terceros que actúan
        como encargados del tratamiento:
      </p>
      <ul>
        <li>
          <strong>Replit (hosting + base de datos + autenticación)</strong>:
          aloja la app, la base de datos PostgreSQL y gestiona el inicio de
          sesión (Replit Auth con Google/Apple). Datos: cuenta, perfil,
          actividad. Más información en{" "}
          <a href="https://replit.com/site/privacy" rel="noopener noreferrer">
            replit.com/site/privacy
          </a>.
        </li>
        <li>
          <strong>Replit Object Storage</strong>: almacena imágenes de perfil
          y otros archivos subidos. Política:{" "}
          <a href="https://replit.com/site/privacy" rel="noopener noreferrer">
            replit.com/site/privacy
          </a>.
        </li>
        <li>
          <strong>Resend</strong>: envía correos transaccionales (avisos,
          notificaciones puntuales). Datos: correo electrónico y contenido
          del mensaje. Política:{" "}
          <a href="https://resend.com/legal/privacy-policy" rel="noopener noreferrer">
            resend.com/legal/privacy-policy
          </a>.
        </li>
        <li>
          <strong>Expo Push</strong>: enruta las notificaciones push hacia
          Apple/Google. Datos: token del dispositivo y resumen del aviso.
          Política:{" "}
          <a href="https://expo.dev/privacy" rel="noopener noreferrer">
            expo.dev/privacy
          </a>.
        </li>
        <li>
          <strong>Apple (APNs)</strong> y <strong>Google (FCM)</strong>:
          entregan finalmente las notificaciones a tu dispositivo.
          Políticas:{" "}
          <a href="https://www.apple.com/legal/privacy/" rel="noopener noreferrer">
            apple.com/legal/privacy
          </a>{" "}
          y{" "}
          <a href="https://policies.google.com/privacy" rel="noopener noreferrer">
            policies.google.com/privacy
          </a>.
        </li>
        <li>
          <strong>PostHog</strong> (solo app móvil): analítica de uso para
          entender qué pantallas usas. Datos: identificador de usuario,
          eventos de uso, pantalla, plataforma. No registramos contraseñas
          ni el contenido de los partidos. Política:{" "}
          <a href="https://posthog.com/privacy" rel="noopener noreferrer">
            posthog.com/privacy
          </a>.
        </li>
        <li>
          <strong>Sentry</strong> (solo app móvil): captura de errores
          técnicos para corregir fallos. Datos: identificador de usuario y
          traza del error. PII desactivada. Política:{" "}
          <a href="https://sentry.io/privacy/" rel="noopener noreferrer">
            sentry.io/privacy
          </a>.
        </li>
      </ul>
      <p>
        No vendemos tus datos personales a terceros con fines publicitarios.
      </p>

      <h2>6. Conservación</h2>
      <p>
        Conservamos tus datos mientras tu cuenta esté activa. Si la eliminas,
        borramos tus datos personales en un plazo razonable y conservamos los
        partidos jugados de forma anonimizada (apareces como "Cuenta
        eliminada") para no romper el historial deportivo de tus rivales.
        Algunos registros técnicos (logs, copias de seguridad) pueden
        permanecer un tiempo adicional limitado.
      </p>

      <h2>7. Tus derechos</h2>
      <p>
        Conforme a la Ley 172-13 de la República Dominicana, tienes derecho
        a <strong>acceder</strong>, <strong>rectificar</strong>,{" "}
        <strong>actualizar</strong>, <strong>oponerte</strong> al
        tratamiento, <strong>eliminar</strong> tus datos personales y a la{" "}
        <strong>portabilidad</strong> (recibir una copia de tus datos en un
        formato estructurado y de uso común). Puedes ejercerlos:
      </p>
      <ul>
        <li>
          Desde la app: edita tu perfil o usa "Eliminar cuenta" en la sección
          Perfil.
        </li>
        <li>
          Por correo:{" "}
          <a href="mailto:contacto@courtvibe.app">contacto@courtvibe.app</a>.
          Para solicitar una copia portable de tus datos, indica
          expresamente "solicitud de portabilidad" en el asunto.
        </li>
      </ul>
      <p>
        Si consideras que no atendemos correctamente tus derechos, tienes
        derecho a presentar una reclamación ante el{" "}
        <strong>Instituto Nacional de Acceso a la Información (INAIPI)</strong>{" "}
        de la República Dominicana, autoridad encargada de velar por el
        cumplimiento de la Ley 172-13 (
        <a href="https://inaipi.gob.do" rel="noopener noreferrer">
          inaipi.gob.do
        </a>
        ).
      </p>

      <h2>7 bis. Cookies y almacenamiento local</h2>
      <p>
        CourtVibe utiliza únicamente cookies y almacenamiento local{" "}
        <strong>estrictamente necesarios</strong> para el funcionamiento del
        servicio (sesión de inicio, preferencias básicas de la app). En
        concreto:
      </p>
      <ul>
        <li>
          <strong>Cookie de sesión</strong> (<code>connect.sid</code>):
          mantiene tu inicio de sesión activo. Se elimina al cerrar sesión.
        </li>
        <li>
          <strong>Almacenamiento local</strong>: guarda preferencias
          (idioma, último club visto, banners ya cerrados) y tokens de
          sesión en la app móvil.
        </li>
      </ul>
      <p>
        No utilizamos cookies de publicidad ni de seguimiento de terceros
        en la web. La analítica solo está activa en la app móvil
        (PostHog) y se identifica por el ID de tu cuenta, no por cookies.
      </p>

      <h2>8. Menores</h2>
      <p>
        CourtVibe no está dirigido a menores de 16 años. Si detectas que un menor
        ha creado una cuenta sin autorización, escríbenos y la eliminaremos.
      </p>

      <h2>9. Seguridad</h2>
      <p>
        Aplicamos medidas técnicas y organizativas razonables (cifrado en
        tránsito, control de accesos, sesiones firmadas). Ningún sistema es
        100% invulnerable; en caso de incidente con riesgo para tus datos,
        seguiremos las obligaciones de notificación aplicables.
      </p>

      <h2>10. Transferencias internacionales</h2>
      <p>
        Algunos proveedores (Replit, Resend, Expo, Apple, Google, PostHog,
        Sentry) procesan datos fuera de la República Dominicana,
        principalmente en EE. UU. y la UE. Al usar CourtVibe aceptas estas
        transferencias bajo las garantías ofrecidas por cada proveedor.
      </p>

      <h2>11. Cambios en esta política</h2>
      <p>
        Podemos actualizar esta política. Si los cambios son sustanciales,
        te avisaremos dentro de la app o por correo electrónico con al
        menos <strong>15 días de antelación</strong> a su entrada en vigor
        y, cuando corresponda, te pediremos volver a aceptar. Si no estás
        de acuerdo con los cambios, puedes eliminar tu cuenta antes de la
        fecha de entrada en vigor.
      </p>

      <p className="text-sm text-muted-foreground">
        Versión {TERMS_VERSION} · {formatDate(TERMS_DATE_ISO, "es")}
      </p>
    </>
  );
}

/* ---------------- English — Terms ---------------- */
function TermsEN() {
  return (
    <>
      <p>
        These Terms govern your use of <strong>CourtVibe</strong>, a web and
        mobile app for the padel community (match logging, rankings, private
        leagues and social features). By using CourtVibe you confirm that you
        accept these Terms.
      </p>

      <h2>1. Operator and contact</h2>
      <p>{COPY.en.operator}</p>
      <p>
        For any inquiry, write to{" "}
        <a href="mailto:contacto@courtvibe.app">contacto@courtvibe.app</a>.
      </p>

      <h2>2. Minimum age</h2>
      <p>
        You must be at least <strong>16 years old</strong> to create an
        account. Users under 18 are encouraged to obtain consent from a
        parent or legal guardian.
      </p>

      <h2>3. User account</h2>
      <ul>
        <li>
          Access is provided through Replit Auth (with providers such as
          Google or Apple) or via email.
        </li>
        <li>
          You are responsible for keeping your credentials confidential and
          for all activity carried out from your account.
        </li>
        <li>
          You must provide truthful information in your profile (name, date
          of birth, level, etc.).
        </li>
      </ul>

      <h2>4. Acceptable use</h2>
      <p>By using CourtVibe you agree NOT to:</p>
      <ul>
        <li>Falsify match results or impersonate other players.</li>
        <li>Harass, threaten or discriminate against other users.</li>
        <li>
          Attempt to compromise the security of the service (mass scraping,
          reverse engineering, DoS attacks, etc.).
        </li>
        <li>
          Upload illegal or offensive content, or content that infringes
          third-party rights.
        </li>
      </ul>
      <p>
        We may suspend or delete accounts that violate these rules, without
        prior notice when needed to protect the community.
      </p>

      <h2>5. User content</h2>
      <p>
        Matches, comments, results, profile photos and other information you
        contribute remain yours. By uploading them you grant us a limited,
        worldwide, royalty-free license to display them inside CourtVibe to other
        users you interact with (opponents, league members, etc.) and to
        operate the service.
      </p>

      <h2>6. Levels and ELO (informational only)</h2>
      <p>
        CourtVibe levels (1.0 – 7.0), global ELO and rankings are{" "}
        <strong>informational and recreational only</strong>. They are a
        community tool, <strong>not an official or federation-issued
        ranking</strong>, and do not replace the rankings of the Dominican
        Padel Federation or any other federation, association or
        professional circuit. Using them for registrations, prizes or
        formal decisions is the sole responsibility of whoever uses them.
      </p>
      <p>
        The calculation is deterministic but may be adjusted in the future
        to improve accuracy; material changes will be communicated in-app.
      </p>

      <h2>7. Availability and service model</h2>
      <p>
        CourtVibe is provided "as is", without guarantees of continuous
        availability. We may interrupt, modify or discontinue features at
        any time, especially during this early phase.
      </p>
      <p>
        The service is currently <strong>free</strong>. In the future we
        may introduce <strong>paid plans</strong> or premium features. Any
        material change to the model (including charging for features that
        are free today) will be communicated in-app or by email at least{" "}
        <strong>15 days in advance</strong> of taking effect, giving you
        the option to discontinue use of those features.
      </p>

      <h2>8. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, the operator is not liable
        for indirect damages, lost profits, data loss or any other harm
        arising from the use or inability to use CourtVibe. Relationships
        between players (in-person matches, payments, injuries, etc.) are
        the sole responsibility of users.
      </p>
      <p>
        In any event, and without prejudice to the above, the operator's
        total aggregate liability to any user, for all claims arising from
        or related to the use of CourtVibe, is limited to a maximum of{" "}
        <strong>one hundred US dollars (US$100)</strong> or its equivalent
        in Dominican pesos.
      </p>

      <h2>9. Account deletion</h2>
      <p>
        You can delete your account at any time from "Profile → Delete
        account". Your personal data is removed; recorded matches are
        retained anonymously as <em>"Deleted account"</em> to preserve your
        opponents' history. See the Privacy Policy for more details.
      </p>

      <h2>10. Intellectual property</h2>
      <p>
        The <em>CourtVibe</em> brand, logo, code and designs are property of the
        operator. They may not be copied or used without prior written
        permission.
      </p>

      <h2>11. Governing law and jurisdiction</h2>
      <p>
        These Terms are governed by the laws of the{" "}
        <strong>Dominican Republic</strong>, in particular Law 172-13 on
        Personal Data Protection. Any dispute will be submitted to the
        competent courts of Santo Domingo.
      </p>

      <h2>12. Changes to the Terms</h2>
      <p>
        We may update these Terms. If changes are material, we will notify
        you in-app or by email and, when applicable, ask you to accept again.
      </p>

      <p className="text-sm text-muted-foreground">
        Version {TERMS_VERSION} · {formatDate(TERMS_DATE_ISO, "en")}
      </p>
    </>
  );
}

/* ---------------- English — Privacy ---------------- */
function PrivacyEN() {
  return (
    <>
      <p>
        At <strong>CourtVibe</strong> we treat your personal data with the same
        care we'd want for our own. This policy explains what data we
        collect, for what purposes, with whom we share it and what rights
        you have. We comply with Dominican Republic Law 172-13 on Personal
        Data Protection.
      </p>

      <h2>1. Data controller</h2>
      <p>{COPY.en.operator}</p>
      <p>
        Contact:{" "}
        <a href="mailto:contacto@courtvibe.app">contacto@courtvibe.app</a>.
      </p>

      <h2>2. Data we collect</h2>
      <ul>
        <li>
          <strong>Account:</strong> first name, last name, email, profile
          photo (when you provide one or when Google/Apple provides it at
          sign-in).
        </li>
        <li>
          <strong>Player profile:</strong> phone, date of birth, gender,
          country, city, home club, dominant hand, playing style, years
          playing, schedule and frequency.
        </li>
        <li>
          <strong>Sports activity:</strong> matches, results, confirmations,
          leagues, friendships, schedules, achievements.
        </li>
        <li>
          <strong>Technical data:</strong> session identifiers, push
          notification tokens, device language, minimal logs for debugging.
        </li>
        <li>
          <strong>Legal acceptance:</strong> we record the date and version
          of the Terms and Privacy Policy you accepted.
        </li>
      </ul>

      <h2>3. Purposes</h2>
      <ul>
        <li>Operate the service (rankings, leagues, matches).</li>
        <li>Send relevant notifications (pending matches, invites).</li>
        <li>Handle support requests and prevent abuse.</li>
        <li>Improve the product (aggregate analytics and debugging).</li>
        <li>Comply with applicable legal obligations.</li>
      </ul>

      <h2>4. Legal basis</h2>
      <p>
        We process data based on your consent (when accepting these Terms
        and this Policy), performance of the service you request, and our
        legitimate interest in keeping CourtVibe safe and functional.
      </p>

      <h2>5. Third parties that help us operate</h2>
      <p>
        Some data is processed on third-party infrastructure acting as data
        processors:
      </p>
      <ul>
        <li>
          <strong>Replit (hosting + database + authentication)</strong>:
          hosts the app, the PostgreSQL database and handles sign-in (Replit
          Auth with Google/Apple). Data: account, profile, activity. More at{" "}
          <a href="https://replit.com/site/privacy" rel="noopener noreferrer">
            replit.com/site/privacy
          </a>.
        </li>
        <li>
          <strong>Replit Object Storage</strong>: stores profile images and
          other uploaded files. Policy:{" "}
          <a href="https://replit.com/site/privacy" rel="noopener noreferrer">
            replit.com/site/privacy
          </a>.
        </li>
        <li>
          <strong>Resend</strong>: sends transactional emails (alerts,
          one-off notifications). Data: email address and message content.
          Policy:{" "}
          <a href="https://resend.com/legal/privacy-policy" rel="noopener noreferrer">
            resend.com/legal/privacy-policy
          </a>.
        </li>
        <li>
          <strong>Expo Push</strong>: routes push notifications to
          Apple/Google. Data: device token and a summary of the
          notification content. Policy:{" "}
          <a href="https://expo.dev/privacy" rel="noopener noreferrer">
            expo.dev/privacy
          </a>.
        </li>
        <li>
          <strong>Apple (APNs)</strong> and <strong>Google (FCM)</strong>:
          deliver the notifications to your device. Policies:{" "}
          <a href="https://www.apple.com/legal/privacy/" rel="noopener noreferrer">
            apple.com/legal/privacy
          </a>{" "}
          and{" "}
          <a href="https://policies.google.com/privacy" rel="noopener noreferrer">
            policies.google.com/privacy
          </a>.
        </li>
        <li>
          <strong>PostHog</strong> (mobile app only): usage analytics to
          understand what screens you use. Data: user id, usage events,
          screen, platform. We do NOT log passwords or match content.
          Policy:{" "}
          <a href="https://posthog.com/privacy" rel="noopener noreferrer">
            posthog.com/privacy
          </a>.
        </li>
        <li>
          <strong>Sentry</strong> (mobile app only): technical error capture
          to fix bugs. Data: user id and error trace. PII disabled. Policy:{" "}
          <a href="https://sentry.io/privacy/" rel="noopener noreferrer">
            sentry.io/privacy
          </a>.
        </li>
      </ul>
      <p>We do not sell your personal data to third parties for advertising.</p>

      <h2>6. Retention</h2>
      <p>
        We retain your data while your account is active. If you delete it,
        we erase your personal data within a reasonable timeframe and keep
        played matches anonymously (you appear as "Deleted account") so we
        don't break your opponents' sports history. Some technical records
        (logs, backups) may remain for a limited additional period.
      </p>

      <h2>7. Your rights</h2>
      <p>
        Under Dominican Republic Law 172-13, you have the right to{" "}
        <strong>access</strong>, <strong>rectify</strong>,{" "}
        <strong>update</strong>, <strong>object</strong> to processing,{" "}
        <strong>delete</strong> your personal data and to{" "}
        <strong>portability</strong> (receive a copy of your data in a
        structured, commonly-used format). You can exercise them:
      </p>
      <ul>
        <li>
          From the app: edit your profile or use "Delete account" in the
          Profile section.
        </li>
        <li>
          By email:{" "}
          <a href="mailto:contacto@courtvibe.app">contacto@courtvibe.app</a>.
          To request a portable copy of your data, write "portability
          request" in the subject line.
        </li>
      </ul>
      <p>
        If you believe we are not honoring your rights, you have the right
        to file a complaint with the{" "}
        <strong>National Institute for Access to Information (INAIPI)</strong>{" "}
        of the Dominican Republic, the authority that oversees compliance
        with Law 172-13 (
        <a href="https://inaipi.gob.do" rel="noopener noreferrer">
          inaipi.gob.do
        </a>
        ).
      </p>

      <h2>7 bis. Cookies and local storage</h2>
      <p>
        CourtVibe uses only <strong>strictly necessary</strong> cookies and
        local storage for the service to work (login session, basic app
        preferences). Specifically:
      </p>
      <ul>
        <li>
          <strong>Session cookie</strong> (<code>connect.sid</code>): keeps
          you logged in. Removed on sign-out.
        </li>
        <li>
          <strong>Local storage</strong>: stores preferences (language,
          last viewed club, dismissed banners) and session tokens in the
          mobile app.
        </li>
      </ul>
      <p>
        We do not use advertising or third-party tracking cookies on the
        web. Analytics is only active in the mobile app (PostHog) and
        identifies users by account ID, not by cookies.
      </p>

      <h2>8. Minors</h2>
      <p>
        CourtVibe is not directed to children under 16. If you find that a minor
        has created an account without authorization, contact us and we will
        delete it.
      </p>

      <h2>9. Security</h2>
      <p>
        We apply reasonable technical and organizational measures
        (encryption in transit, access control, signed sessions). No system
        is 100% invulnerable; in case of an incident with risk to your data,
        we will follow applicable notification obligations.
      </p>

      <h2>10. International transfers</h2>
      <p>
        Some providers (Replit, Resend, Expo, Apple, Google, PostHog,
        Sentry) process data outside the Dominican Republic, mainly in the
        US and EU. By using CourtVibe you accept these transfers under the
        safeguards offered by each provider.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We may update this policy. If changes are material, we will notify
        you in-app or by email at least <strong>15 days in advance</strong>
        of taking effect and, when applicable, ask you to accept again. If
        you disagree with the changes, you may delete your account before
        the effective date.
      </p>

      <p className="text-sm text-muted-foreground">
        Version {TERMS_VERSION} · {formatDate(TERMS_DATE_ISO, "en")}
      </p>
    </>
  );
}
